import * as React from "react";
import { Link, useRouteLoaderData } from "react-router";
import { useTranslation } from "react-i18next";

import {
  AppBar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import Language from "@mui/icons-material/Language";
import Person from "@mui/icons-material/Person";
import SyncIcon from "@mui/icons-material/Sync";
import MenuIcon from "@mui/icons-material/Menu";
import Logout from "@mui/icons-material/Logout";
import EmailIcon from "@mui/icons-material/Email";
import GroupsIcon from "@mui/icons-material/Groups";

import { useAuth } from "../../API/auth";

import Logo from "../Logo/Logo";
import Breadcrumbs from "../Breadcrumbs/Breadcrumb";

import { supportedLngs } from "../../i18n/I18next";

import styles from "./NavBar.module.css";
import { getAgroManagerUrl } from "../../API/agromanager/agromanager";
import { useIsAllowedAgromanagerUser } from "../AuthGuard/useIsAllowedAgromanagerUser";
import { FieldsData } from "../../API/fields";

function displayLanguage(language: string): string {
  const display = new Intl.DisplayNames(language, {
    type: "language",
  });
  const [initial, ...rest] = [...(display.of(language) as string)];
  return [initial.toLocaleUpperCase(language), ...rest].join("");
}

interface NavBarProps {
  drawerWidth?: number;
  handleDrawerToggle?: () => void;
  hasDrawer?: boolean;
}

export default function NavBar({
  handleDrawerToggle,
  drawerWidth = 0,
  hasDrawer = true,
}: NavBarProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { i18n } = useTranslation();
  const auth = useAuth();

  const handleMenuLang = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseLanguage = () => {
    setAnchorEl(null);
  };

  return (
    <AppBar
      className={styles.appBar}
      sx={{
        width: { xs: "100%", sm: `calc(100% - ${drawerWidth}px)` },
      }}
    >
      <div className={styles.breadcrumbs}>
        {hasDrawer && (
          <>
            <IconButton
              className={styles.toggleButton}
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ display: { xs: "flex", sm: "none" } }}
            >
              <MenuIcon />
            </IconButton>
            <Breadcrumbs mobileView={false} />
          </>
        )}

        <Box
          className={styles.logoContainer}
          sx={{
            display: {
              xs: "flex",
              sm: hasDrawer ? "none" : "flex",
            },
          }}
        >
          <Link to="">
            <Logo width={90} height={38} />
          </Link>
        </Box>
      </div>
      <div className={styles.toolBar}>
        <Button
          onClick={handleMenuLang}
          className={styles.navItem}
          color="inherit"
        >
          <div className={styles.navItemIcon}>
            <Language />
          </div>
          <div className={styles.navItemWrapper}>
            <div className={styles.navItemText}>
              {i18n.language.toLocaleUpperCase(i18n.language)}
            </div>
            <KeyboardArrowDown />
          </div>
        </Button>
        <Menu
          id="menu-language"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: 48,
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleCloseLanguage}
        >
          {supportedLngs.map((language) => {
            return (
              <MenuItem
                key={language}
                onClick={() => i18n.changeLanguage(language)}
              >
                {displayLanguage(language)}
              </MenuItem>
            );
          })}
        </Menu>
        <div className={styles.navSpacer}>
          {auth.username ? <LoggedInMenu /> : <LoggedOutMenu />}
        </div>
      </div>
    </AppBar>
  );
}

function LoggedOutMenu() {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { t } = useTranslation();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseLogin = () => {
    setAnchorEl(null);
  };
  return (
    <>
      <Button
        className={styles.navItem}
        aria-label="account of logged out user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit"
      >
        <div className={styles.navItemIcon}>
          <Person />
        </div>
        <div className={styles.navItemWrapper}>
          <div className={styles.navItemText}>{t("Menu.login")}</div>
          <KeyboardArrowDown />
        </div>
      </Button>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: 48,
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseLogin}
      >
        <MenuItem>{t("Menu.Create account")}</MenuItem>
      </Menu>
    </>
  );
}

export function LoggedInMenu() {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { t } = useTranslation();
  const auth = useAuth();
  const customer = useRouteLoaderData("customer") as FieldsData;
  const customerName = customer?.customer.name;

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseLogin = () => {
    setAnchorEl(null);
  };

  const handleSwitchEnvironment = () => {
    handleCloseLogin();
    window.location.href = getAgroManagerUrl();
  };

  return (
    <>
      <Button
        className={styles.navItem}
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit"
      >
        <div className={styles.navItemIcon}>
          <Person />
        </div>
        <div className={styles.navItemWrapper}>
          <KeyboardArrowDown />
        </div>
      </Button>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: 48,
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseLogin}
      >
        <MenuItem data-dd-privacy="mask">
          <ListItemIcon>
            <EmailIcon fontSize="small" />
          </ListItemIcon>
          {auth.username}
        </MenuItem>

        {customerName && (
          <Link to="/" className={styles.menuLink}>
            <MenuItem>
              <ListItemIcon>
                <GroupsIcon fontSize="small" />
              </ListItemIcon>
              {customerName}
            </MenuItem>
          </Link>
        )}

        {useIsAllowedAgromanagerUser() ? (
          <MenuItem onClick={handleSwitchEnvironment}>
            <ListItemIcon>
              <SyncIcon fontSize="small" />
            </ListItemIcon>
            {t("menu.switch_environment")}
            {auth.agroManagerEnv && ` (${auth.agroManagerEnv})`}
          </MenuItem>
        ) : null}

        <hr
          style={{
            margin: "4px 0",
            border: "0",
            borderTop: "1px solid var(--color-border)",
          }}
        />

        <MenuItem onClick={auth.logout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          {t("menu.Logout")}
        </MenuItem>
      </Menu>
    </>
  );
}
